// src/pages/StoreDetailPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { showError } from '../components/Alert';
import ProductList from '../components/ProductList';
import Pagination from '../components/Pagination';
import FilterSidebar, { Filters } from '../components/FilterSideBar';
import '../styles/StoreDetailPage.css';
import type { StoreDTO } from '../dto/StoreDTO';
import type { ItemDTO } from '../dto/item/ItemDTO';
import { fetchStoreById, fetchStoreItems } from '../services/store';

const StoreDetailPage: React.FC = () => {
  // 1️⃣ read the "storeId" from the URL
  const { storeId } = useParams<{ storeId: string }>();
  // decode once so we never have "%20"
  const decodedId = storeId ? decodeURIComponent(storeId) : '';

  const [store, setStore] = useState<StoreDTO | null>(null);
  const [products, setProducts] = useState<ItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(12);

  const [filters, setFilters] = useState<Filters>({
    category: [],
    color: [],
    size: [],
  });

  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const perPage = 12;

  // 2️⃣ fetch store details once we have a decodedId
  useEffect(() => {
    if (!decodedId) return;

    fetchStoreById(decodedId)
      .then((r) => setStore(r.data))
      .catch(() => showError('Could not load store details.'));
  }, [decodedId]);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 700;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) {
        setIsMobileFilterOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const buildStoreParams = useCallback(
    (storeKey: string, activeFilters: Filters, targetPage: number) => {
      const category = activeFilters.category.find((c) => c.trim());
      const colors = activeFilters.color
        .map((c) => c.trim().toLowerCase())
        .filter(Boolean);
      const sizes = activeFilters.size.map((s) => s.trim()).filter(Boolean);

      return {
        storeId: storeKey,
        category: category?.trim(),
        colors: colors.length ? colors : undefined,
        sizes: sizes.length ? sizes : undefined,
        page: targetPage,
      };
    },
    []
  );

  const loadItems = useCallback(
    async (nextFilters: Filters, targetPage: number) => {
      if (!decodedId) return;

      setLoading(true);
      try {
        const params = buildStoreParams(decodedId, nextFilters, targetPage);
        const res = await fetchStoreItems(params);
        const data = res.data;
        const rawItems = Array.isArray(data?.items) ? data.items : [];
        setProducts(rawItems);
        setTotalItems(data?.total ?? rawItems.length);
        setPageSize(data?.pageSize ?? 12);
        setPage(data?.page ?? targetPage);
      } catch (err) {
        console.error('[StoreDetailPage] Failed to load store items:', err);
        showError('Could not load products.');
        setProducts([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [buildStoreParams, decodedId]
  );

  const resetFilters = useCallback(() => {
    const cleared: Filters = { category: [], color: [], size: [] };
    setFilters(cleared);
    setPage(1);
    loadItems(cleared, 1);
    setIsMobileFilterOpen(false);
  }, [loadItems]);

  // 3️⃣ fetch that store's items on load
  useEffect(() => {
    if (!decodedId) return;
    setPage(1);
    loadItems(filters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedId, loadItems]);

  // 4️⃣ update filters (except "store") and call backend search
  const onFilterChange = (filterType: keyof Filters, value: string) => {
    setFilters((prev) => {
      const alreadySelected = prev[filterType].includes(value);
      const updatedList = alreadySelected
        ? prev[filterType].filter((x) => x !== value)
        : [...prev[filterType], value];

      const nextFilters = {
        ...prev,
        [filterType]: updatedList,
      } as Filters;

      setPage(1);
      loadItems(nextFilters, 1);
      return nextFilters;
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    loadItems(filters, nextPage);
  };

  // guard for missing/invalid param
  if (!decodedId) return <p>Invalid store identifier.</p>;
  if (!store) return <p>Loading store…</p>;

  return (
    <div className="store-detail-page">
      <div className="store-header">
        <div className="store-info">
          <h1>{store.name}</h1>
          <p className="store-description">
            {store.description ||
              'Feminine vintage-inspired womenswear made ethically from natural fibres in Vancouver, Canada. Our design process is intentionally centred on creating timeless pieces that you will wear for years to come.'}
          </p>
        </div>
        <div className="store-images">
          <img src={store.thumbnail} alt={store.name} className="store-thumb" />
        </div>
      </div>

      <div className="products-section">
        <div className="products-content">
          {!isMobile && (
            <FilterSidebar
              filters={filters}
              onFilterChange={onFilterChange}
              onResetFilters={resetFilters}
            />
          )}
          <div className="store-products products-list-container">
            <div className="products-header">
              <h3 className="num-products">{products.length} Products</h3>
            </div>
            <div className="number-sort-container">
              <div className="products-sort">
                {isMobile && (
                  <button
                    type="button"
                    className="filter-button"
                    onClick={() => setIsMobileFilterOpen(true)}
                  >
                    Filter
                  </button>
                )}
                <select className="form-control">
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name-az">Name: A to Z</option>
                  <option value="name-za">Name: Z to A</option>
                </select>
              </div>
            </div>
            {loading ? (
              <p>Loading products…</p>
            ) : (
              <>
                <ProductList products={products} />
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={pageSize || perPage}
                  currentPage={page}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <>
          <div
            className={`filter-overlay ${isMobileFilterOpen ? 'open' : ''}`}
            onClick={() => setIsMobileFilterOpen(false)}
          />
          <div
            className={`filter-sidebar-mobile ${
              isMobileFilterOpen ? 'open' : ''
            }`}
          >
            <div className="filter-sidebar-mobile__header">
              <h3 className="filter-sidebar-mobile__title">Filters</h3>
              <button
                type="button"
                className="filter-sidebar-mobile__close"
                onClick={() => setIsMobileFilterOpen(false)}
                aria-label="Close filters"
              >
                x
              </button>
            </div>
            <div className="filter-sidebar-mobile__content">
              <FilterSidebar
                filters={filters}
                onFilterChange={onFilterChange}
                onResetFilters={resetFilters}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreDetailPage;
